/**
 * Date Filter Calculator Module
 *
 * Handles date range calculation for different filter types (YTD, MTD, QTD, etc.)
 * Implements Single Responsibility Principle by separating date logic from UI logic
 *
 * @module filterDateCalculator
 */

/**
 * Filter type enum - defines supported filter date types
 * Extensible design allows adding new filter types without modifying existing code (Open/Closed Principle)
 */
export enum FilterDateType {
  YTD = 'YTD',
  MTD = 'MTD',
  QTD = 'QTD',
  CUSTOM = 'CUSTOM',
  STATIC = 'STATIC',
}

/**
 * Filter metadata interface
 * Defines contract for filter items passed to date calculator
 * Interface Segregation Principle: Only includes properties needed for date calculation
 */
export interface FilterItem {
  displayName: string;
  isEditable: boolean;
  startDate?: string;
  endDate?: string;
  filterType?: string;
  type?: string;
  [key: string]: any;
}

/**
 * Date range result interface
 * Clean contract for returned date adjustments
 */
export interface DateRangeAdjustment {
  startDate?: string;
  endDate?: string;
}

/**
 * Determine filter type from filter item
 * Dependency Inversion Principle: Depend on FilterDateType enum, not magic strings
 *
 * @param filter - Filter item to analyze
 * @returns FilterDateType enum value
 */
function getFilterDateType(filter: FilterItem): FilterDateType {
  const filterType = (filter.filterType ?? filter.type ?? '').toUpperCase();

  if (filterType.includes('YTD') || filterType === 'YEARTODATE') return FilterDateType.YTD;
  if (filterType.includes('MTD') || filterType === 'MONTHTODATE') return FilterDateType.MTD;
  if (filterType.includes('QTD') || filterType === 'QUARTERTODATE') return FilterDateType.QTD;
  if (filterType.includes('CUSTOM')) return FilterDateType.CUSTOM;

  return FilterDateType.STATIC;
}

/**
 * Calculate start date based on filter type
 * Pure function with no side effects
 * Liskov Substitution Principle: All filter types behave consistently through this interface
 *
 * @param filterType - Type of filter requesting date calculation
 * @param today - Reference date for calculations (defaults to current date)
 * @returns Calculated start date, or null if date should not be modified
 *
 * @example
 * calculateStartDate(FilterDateType.YTD) // Returns Jan 1 of current year
 * calculateStartDate(FilterDateType.MTD) // Returns 1st of current month
 */
function calculateStartDate(filterType: FilterDateType, today: Date = new Date()): Date | null {
  switch (filterType) {
    case FilterDateType.YTD:
      // Year To Date: January 1 of current year
      return new Date(today.getFullYear(), 0, 1);

    case FilterDateType.MTD:
      // Month To Date: 1st of current month
      return new Date(today.getFullYear(), today.getMonth(), 1);

    case FilterDateType.QTD: {
      // Quarter To Date: 1st of current quarter
      const quarter = Math.floor(today.getMonth() / 3);
      return new Date(today.getFullYear(), quarter * 3, 1);
    }

    case FilterDateType.CUSTOM:
      // Custom ranges: preserve existing dates
      return null;

    case FilterDateType.STATIC:
    default:
      // Pre-calculated dates: don't modify
      return null;
  }
}

/**
 * Format date as MM/DD/YYYY for API consumption
 * Consistent date formatting across application
 *
 * @param date - Date to format
 * @returns Formatted date string in MM/DD/YYYY format
 */
function formatDateForAPI(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Main entry point: Apply date modifications only when needed
 * Single Responsibility Principle: This function has ONE responsibility - calculate correct dates
 * Open/Closed Principle: New filter types extend the enum and switch statement without modifying existing logic
 *
 * @param filter - Filter item to adjust dates for
 * @param isEditable - Whether the filter is editable
 * @returns Object containing startDate and/or endDate adjustments (empty if no changes needed)
 *
 * @example
 * const adjustment = getAdjustedFilterDate(
 *   { displayName: 'YTD', isEditable: true, filterType: 'YTD' },
 *   true
 * );
 * // Returns: { startDate: '1/1/2026', endDate: '8/28/2026' }
 */
export function getAdjustedFilterDate(
  filter: FilterItem,
  isEditable: boolean
): DateRangeAdjustment {
  // Only modify dates for editable filters
  if (!isEditable) {
    return {};
  }

  const filterType = getFilterDateType(filter);

  // Don't modify static or custom ranges
  if (filterType === FilterDateType.STATIC || filterType === FilterDateType.CUSTOM) {
    return {};
  }

  const startDate = calculateStartDate(filterType);

  // If no start date to calculate, return empty
  if (!startDate) {
    return {};
  }

  return {
    startDate: formatDateForAPI(startDate),
    // End date is always today for YTD/MTD/QTD
    endDate: formatDateForAPI(new Date()),
  };
}

/**
 * Validate date range makes logical sense
 * Error handling: Catch invalid ranges before they reach API
 *
 * @param startDate - Start date string in any parseable format
 * @param endDate - End date string in any parseable format
 * @returns Validation result with error message if invalid
 *
 * @example
 * const result = validateDateRange('1/1/2026', '8/28/2026');
 * if (!result.isValid) {
 *   console.error(result.error);
 * }
 */
export function validateDateRange(
  startDate: string,
  endDate: string
): { isValid: boolean; error?: string } {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Validate dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return {
        isValid: false,
        error: 'Invalid date format',
      };
    }

    // Validate start is not after end
    if (start > end) {
      return {
        isValid: false,
        error: 'Start date cannot be after end date',
      };
    }

    return { isValid: true };
  } catch (e) {
    return {
      isValid: false,
      error: `Date validation failed: ${e instanceof Error ? e.message : 'Unknown error'}`,
    };
  }
}
